import * as THREE from 'three';

const _mix = new THREE.Color();
const VOID = new THREE.Color('#c5d0ee');

function bunchTint(bunch, into) {
  const raw = bunch?.userData?.tint;
  if (typeof raw === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.trim())) {
    return into.set(raw.trim());
  }
  const u = bunch?.children?.find((c) => c.material?.uniforms?.uTint)?.material.uniforms.uTint.value;
  if (u?.r != null) return into.copy(u);
  return null;
}

/** Soft ellipse weight for a nebula bunch. 0 = outside the painted cloud. */
export function cloudWeightAt(bunch, x, y) {
  if (!bunch) return 0;
  const cx = bunch.position.x + (bunch.userData.contentOffX ?? 0);
  const cy = bunch.position.y + (bunch.userData.contentOffY ?? 0);
  const hw = Math.max(bunch.userData.halfW || bunch.userData.contentW || 0, 0.45);
  const hh = Math.max(bunch.userData.halfH || bunch.userData.contentH || 0, 0.45);
  const nx = (x - cx) / hw;
  const ny = (y - cy) / hh;
  const d2 = nx * nx + ny * ny;
  if (d2 >= 1.2) return 0;
  return Math.max(0, 1 - d2 / 1.2);
}

/**
 * Colour of the island cloud under (x, y). Overlapping bunches blend.
 * If the path is in open void, uses `fallback` (mission tag, or a cool grey).
 */
export function sampleCloudTint(nebula, x, y, out = new THREE.Color(), fallback = VOID) {
  out.copy(fallback || VOID);
  const bunches = nebula?.children;
  if (!bunches?.length) return out;
  let wSum = 0;
  let r = 0;
  let g = 0;
  let b = 0;
  for (let i = 0; i < bunches.length; i += 1) {
    const bunch = bunches[i];
    if (bunch.userData?.clusterKey === '__none__') continue;
    const w = cloudWeightAt(bunch, x, y);
    if (w <= 0.01) continue;
    if (!bunchTint(bunch, _mix)) continue;
    r += _mix.r * w;
    g += _mix.g * w;
    b += _mix.b * w;
    wSum += w;
  }
  if (wSum < 0.04) return out;
  out.setRGB(r / wSum, g / wSum, b / wSum);
  return out;
}

/** Keep dark tag colours bright enough to read as sparks. */
export function liftSparkTint(color, out = color) {
  out.copy(color);
  const hsl = { h: 0, s: 0, l: 0 };
  out.getHSL(hsl);
  if (hsl.l < 0.52) out.setHSL(hsl.h, Math.min(hsl.s * 1.04, 0.82), 0.56);
  return out;
}
